<script lang="ts">
  import { api } from '../api/client';
  import type { Place } from '../api/types';
  import { groups } from '../stores/groups';
  import { clockAt } from '../utils/checkin';
  import type { ShareLink } from '../utils/sharelinks';
  import Modal from './Modal.svelte';
  import { t } from '../i18n/index.ts';

  // ShareLinks makes, copies, and stops links that show the member's location
  // to someone without an account for a while (spec 001 FR-086, 001 FR-090).
  // The map opens it from its line for active links, and hears of each link
  // made or stopped here through onchange (001 FR-114).
  let { open = $bindable(false), onchange }: { open?: boolean; onchange?: () => void } = $props();

  let links = $state<ShareLink[]>([]);
  let places = $state<Place[]>([]);
  let lasts = $state('4');
  let made = $state('');
  let copied = $state(false);
  let error = $state('');

  $effect(() => {
    if (open) load();
  });

  async function load() {
    made = '';
    error = '';
    try {
      links = await api.get<ShareLink[]>('/me/share-links');
      const gs = await groups.load();
      places = (await Promise.all(gs.map((g) => api.get<Place[]>(`/groups/${g.id}/places`)))).flat().sort((a, b) => a.name.localeCompare(b.name));
    } catch (e: any) {
      error = e.message;
    }
  }

  async function make() {
    error = '';
    copied = false;
    try {
      const body = lasts.startsWith('place:') ? { place_id: lasts.slice(6) } : { hours: Number(lasts) };
      const got = await api.post<ShareLink & { token: string }>('/me/share-links', body);
      made = `${window.location.origin}/s/${got.token}`;
      links = await api.get<ShareLink[]>('/me/share-links');
      onchange?.();
    } catch (e: any) {
      error = e.message;
    }
  }

  async function copy() {
    await navigator.clipboard?.writeText(made).catch(() => {});
    copied = true;
  }

  async function stop(l: ShareLink) {
    try {
      await api.delete(`/me/share-links/${l.id}`);
      links = links.filter((x) => x.id !== l.id);
      onchange?.();
    } catch (e: any) {
      error = e.message;
    }
  }
</script>

<button class="share-links-button card" title={t('componentsShareTitle')} aria-label={t('componentsShareTitle')} onclick={() => (open = true)}>
  <i aria-hidden="true" class="las la-link"></i>
</button>

{#if open}
  <Modal title={t('componentsShareTitle')} onclose={() => (open = false)}>
    <div class="space-y-4 text-sm">
      <p class="text-text-muted">{t('componentsShareIntro')}</p>
      {#if error}<p style="color: var(--theme-error);">{error}</p>{/if}
      {#if links.length > 0}
        <div class="space-y-1">
          <p class="font-semibold">{t('componentsShareActive')}</p>
          {#each links as l (l.id)}
            <div class="flex items-center justify-between gap-2">
              <span>{l.place_name ? t('componentsShareMadeUntilPlace', { made: clockAt(l.created_at), place: l.place_name, ends: clockAt(l.ends_at) }) : t('componentsShareMadeUntil', { made: clockAt(l.created_at), ends: clockAt(l.ends_at) })}</span>
              <button class="btn btn-secondary btn-sm" onclick={() => stop(l)}>{t('componentsShareStop')}</button>
            </div>
          {/each}
        </div>
      {/if}
      {#if made}
        <div class="space-y-2">
          <input class="input w-full" readonly value={made} aria-label={t('componentsShareLinkLabel')} onfocus={(e) => e.currentTarget.select()} />
          <button class="btn btn-primary" onclick={copy}>{copied ? t('appCopied') : t('componentsShareCopyLink')}</button>
        </div>
      {:else}
        <label class="flex flex-wrap items-center gap-2">
          <span>{t('componentsShareFor')}</span>
          <select class="input py-1" bind:value={lasts} aria-label={t('componentsShareFor')}>
            <option value="1">{t('componentsShareHours', { n: 1 })}</option>
            <option value="4">{t('componentsShareHours', { n: 4 })}</option>
            <option value="24">{t('componentsShareDays', { n: 1 })}</option>
            {#each places as p (p.id)}<option value={`place:${p.id}`}>{t('componentsShareUntilReach', { place: p.name })}</option>{/each}
          </select>
          <button class="btn btn-primary" onclick={make}>{t('componentsShareMake')}</button>
        </label>
      {/if}
    </div>
  </Modal>
{/if}

<style>
  .share-links-button {
    width: 3rem;
    height: 3rem;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    color: var(--theme-primary);
  }
</style>
